/**
 * PrivacySettingsPage component
 * Requirements: 10.1, 10.2, 10.3, 10.4 - Unified privacy management UI
 */
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Divider,
  Button,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PolicyIcon from '@mui/icons-material/Policy';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoIcon from '@mui/icons-material/Info';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DataExportButton } from './DataExportButton';
import { AccountDeletionDialog } from './AccountDeletionDialog';
import { useAuth } from '../../auth';

export const PrivacySettingsPage = () => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleAccountDeleted = async () => {
    // Logout and redirect to login page after account deletion
    await logout();
    navigate('/login');
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        プライバシー設定
      </Typography>

      {/* Data Export Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          データエクスポート
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          すべての個人データをJSON形式でダウンロードできます。
          エクスポートには、アカウント情報、回答履歴、分析結果が含まれます。
        </Typography>
        <DataExportButton />
      </Paper>

      {/* Account Deletion Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          アカウント削除
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            この操作は取り消せません。すべてのデータが完全に削除されます。
          </Typography>
        </Alert>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteForeverIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          アカウントを削除
        </Button>
      </Paper>

      {/* Legal Pages Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          法的情報
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to="/privacy/policy"
            >
              <ListItemIcon>
                <PolicyIcon />
              </ListItemIcon>
              <ListItemText primary="プライバシーポリシー" />
              <ChevronRightIcon color="action" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to="/privacy/terms"
            >
              <ListItemIcon>
                <GavelIcon />
              </ListItemIcon>
              <ListItemText primary="利用規約" />
              <ChevronRightIcon color="action" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to="/privacy/data-usage"
            >
              <ListItemIcon>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText primary="データの収集と使用について" />
              <ChevronRightIcon color="action" />
            </ListItemButton>
          </ListItem>
        </List>
      </Paper>

      <AccountDeletionDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={handleAccountDeleted}
      />
    </Container>
  );
};
